import { FunctionComponent, useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { styles } from "../../../styles/themes/style";
import Text from "../../Text";
import Input from "../../Input";
import Button from "../../Button";
import { IKanban } from "../../../interfaces/models/kanban";
import SvgFragment from "../../../fragments/SvgFragment";
import { useAppHandlers } from "../../../services/actions/app/useAppHandlers";
import { useAppActions } from "../../../stores/application/useAppActions";
import ActionText from "../../ActionText";
import { EmojiPicker } from "../../../fragments/Emoji";

interface IEditKanbanModalProps {
  kanban?: IKanban; // If provided, it's edit mode. If undefined, create mode.
  onConfirm: (data: {
    name: string;
    description: string;
    color: string;
  }) => Promise<void>;
  onClose?: () => void;
  onDelete?: () => void;
  allKanbansCount?: number; // Total number of kanbans in the workspace
}

// List of emojis to cycle through for new pipelines (from Smileys & People category)
const PIPELINE_EMOJIS = ["😀", "😎", "😊", "🤩", "😃", "😄", "🥳", "😁", "🙂", "😉", "🤗", "😇", "🤠", "🥰", "😍"];

const getNextPipelineEmoji = () => {
  const lastIndex = parseInt(localStorage.getItem('lastPipelineEmojiIndex') || '0', 10);
  const nextIndex = (lastIndex + 1) % PIPELINE_EMOJIS.length;
  localStorage.setItem('lastPipelineEmojiIndex', nextIndex.toString());
  return PIPELINE_EMOJIS[nextIndex];
};

const EditKanbanModal: FunctionComponent<IEditKanbanModalProps> = ({
  kanban,
  onConfirm,
  onClose,
  onDelete,
  allKanbansCount = 0,
}) => {
  const { hideModal } = useAppHandlers();
  const { setSidebar } = useAppActions();
  // For new pipelines, start with a different emoji each time
  const initialName = kanban?.name || (getNextPipelineEmoji() + " ");
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(kanban?.description || "");
  const [color, setColor] = useState((kanban as any)?.color || styles.colors.primary600);
  const [isLoading, setIsLoading] = useState(false);

  // Close all sidebars when modal opens
  useEffect(() => {
    setSidebar([]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract all emojis from the beginning of name
  const { emoji, nameWithoutEmoji } = useMemo(() => {
    if (!name) return { emoji: "", nameWithoutEmoji: "" };
    
    // Match all emoji characters and sequences at the start
    // This includes emoji sequences with zero-width joiners, skin tones, variation selectors, etc.
    // Pattern matches: emoji chars, ZWJ (200D), variation selector (FE0F), combining chars, etc.
    let emojiEndIndex = 0;
    let i = 0;
    
    while (i < name.length) {
      const char = name[i];
      const code = char.charCodeAt(0);
      
      // Check for emoji-related characters
      const isEmojiRelated = 
        // Surrogate pairs (most emojis)
        (code >= 0xD800 && code <= 0xDBFF) ||
        // Basic emoji/symbol ranges
        (code >= 0x2600 && code <= 0x26FF) ||
        (code >= 0x2700 && code <= 0x27BF) ||
        (code >= 0x2190 && code <= 0x21FF) ||
        (code >= 0x2300 && code <= 0x23FF) ||
        (code >= 0x2B00 && code <= 0x2BFF) ||
        (code >= 0x25A0 && code <= 0x25FF) ||
        // Variation selector
        code === 0xFE0F ||
        // Zero-width joiner (for sequences)
        code === 0x200D ||
        // Combining enclosing keycap
        code === 0x20E3 ||
        // Gender and other symbols
        (code >= 0x2640 && code <= 0x2642) ||
        (code >= 0x2695 && code <= 0x2696) ||
        code === 0x2708 ||
        code === 0x2764;
      
      if (isEmojiRelated) {
        // Handle surrogate pairs
        if (code >= 0xD800 && code <= 0xDBFF && i + 1 < name.length) {
          i += 2;
        } else {
          i += 1;
        }
        emojiEndIndex = i;
      } else {
        // Stop at first non-emoji character
        break;
      }
    }
    
    if (emojiEndIndex > 0) {
      const detectedEmojis = name.slice(0, emojiEndIndex);
      let restOfName = name.slice(emojiEndIndex);
      // Remove only the first space after emojis (if exists), but keep trailing spaces
      if (restOfName.startsWith(" ")) {
        restOfName = restOfName.slice(1);
      }
      return { emoji: detectedEmojis, nameWithoutEmoji: restOfName };
    }
    
    return { emoji: "", nameWithoutEmoji: name };
  }, [name]);

  const handleEmojiSelect = (selectedEmoji: string) => {
    setName(selectedEmoji + (nameWithoutEmoji ? " " + nameWithoutEmoji : ""));
  };

  useEffect(() => {
    if (kanban) {
      setName(kanban.name);
      setDescription(kanban.description);
      setColor((kanban as any)?.color || styles.colors.primary600);
    }
  }, [kanban]);

  const handleSubmit = async () => {
    // Use emoji or default "😎" if no emoji is present
    const finalEmoji = emoji || "😎";
    const trimmedName = nameWithoutEmoji.trim();
    if (!trimmedName) return;
    const fullName = finalEmoji + " " + trimmedName;
    setIsLoading(true);
    await onConfirm({ name: fullName, description, color });
    setIsLoading(false);
    hideModal();
    if (onClose) onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      const target = e.target as HTMLElement;
      if (target.tagName === "TEXTAREA" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      } else if (target.tagName === "INPUT") {
        e.preventDefault();
        handleSubmit();
      }
    }
  };

  return (
    <Container onKeyDown={handleKeyDown}>
      <Header>
        <Text $heading3>
          {kanban ? "Edit pipeline" : "Create new pipeline"}
        </Text>
        <CloseIcon
          onClick={() => {
            hideModal();
            if (onClose) onClose();
          }}
        >
          <SvgFragment type="close" />
        </CloseIcon>
      </Header>
      <Body>
        <InputWrapper>
          <Label>Pipeline Name</Label>
          <NameInputContainer>
            <EmojiPickerWrapper>
              <EmojiPicker
                onSelect={handleEmojiSelect}
                zIndex={10000002}
                openOnHover={true}
                trigger={
                  <EmojiTrigger>
                    {emoji || "😎"}
                  </EmojiTrigger>
                }
              />
            </EmojiPickerWrapper>
            <StyledInput
              value={nameWithoutEmoji}
              onChangeHandler={(value: string) => setName((emoji ? emoji + " " : "") + value)}
              placeholder="Enter pipeline name"
              autoFocus
              $hasEmoji={!!emoji}
            />
          </NameInputContainer>
        </InputWrapper>
        <InputWrapper>
          <Label>Pipeline Description</Label>
          <StyledTextArea
            value={description}
            onChange={(e: any) => setDescription(e.target.value)}
            placeholder="Enter pipeline description"
            rows={3}
          />
        </InputWrapper>
      </Body>
      <Footer>
        <Button
          $type="blue"
          $width={kanban ? "90px" : "146px"}
          isLoading={isLoading}
          disabled={!name.trim()}
          onClickHandler={handleSubmit}
        >
          {kanban ? "Save" : "Create new pipeline"}
        </Button>
        {kanban && allKanbansCount > 1 && (
          <ActionText
            danger={true}
            onClickHandler={() => {
              hideModal();
              if (onDelete) {
                onDelete();
              }
            }}
          >
            Delete
          </ActionText>
        )}
      </Footer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background: white;
  border-radius: 8px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseIcon = styled.div`
  position: absolute;
  right: 20px;
  cursor: pointer;

  & .svg {
    height: 24px;
    width: 24px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled(Text)`
  font-size: 11px;
  font-weight: 400;
  color: ${styles.colors.black400};
  text-transform: uppercase;
  line-height: 16px;
  letter-spacing: 0.5px;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px 12px;
  border: 1px solid ${styles.colors.black200};
  border-radius: 4px;
  resize: vertical;
  font-family: "Nunito Sans", sans-serif;
  font-size: 14px;
  color: ${styles.colors.black600};
  &:focus {
    outline: none;
    border-color: ${styles.colors.primary600};
  }
  &::placeholder {
    color: ${styles.colors.black300};
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  gap: 16px;
`;

const NameInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const EmojiPickerWrapper = styled.div`
  position: absolute;
  left: 9px;
  top: 6px;
  z-index: 1;
  display: flex;
  align-items: center;
`;

const EmojiTrigger = styled.div`
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  position: relative;

  &:hover {
    background: ${styles.colors.primary100};
  }
`;

const StyledInput = styled(Input)<{ $hasEmoji?: boolean }>`
  ${({ $hasEmoji }) => $hasEmoji && `
    padding-left: 40px;
  `}
  ${({ $hasEmoji }) => !$hasEmoji && `
    padding-left: 40px;
  `}
`;

export default EditKanbanModal;
